import { DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material';
import { Dialog, OutlinedButton } from '@shared/components';
import { useAppTFunction } from '@retail/app/i18n';
import { FormProvider, useForm } from 'react-hook-form';
import {
  defaultPriceListValues,
  priceListSchema,
  PriceListSchema,
  PriceRoundingConfigTable,
  PriceRoundingSimulations,
} from '@retail/rounding/components';
import { withDialogSuspenseErrorBoundary } from '@retail/components';
import { useSimulatePriceRoundingRuleSet } from '@retail/rounding/data-access';
import React, { useCallback, useEffect, useState } from 'react';
import {
  PriceRoundingRuleConfig,
  SimulatePriceRoundingRuleConfigDTO,
} from '@retail/rounding/types';
import { useToast } from '@retail/hooks';
import { yupResolver } from '@hookform/resolvers/yup';

interface Props {
  open: boolean;
  onClose: () => void;
  priceRoundingRuleConfig: PriceRoundingRuleConfig;
  renderHeader: () => React.ReactNode;
}

const ViewRoundingRuleDialog = ({
  open,
  onClose,
  priceRoundingRuleConfig,
  renderHeader,
}: Props) => {
  const t = useAppTFunction();
  const toast = useToast();
  const [hasFetchedInitialSimulation, setHasFetchedInitialSimulation] = useState(false);

  const {
    mutateAsync: mutateSimulateRoundingRuleSet,
    data: simulationResult,
    isPending: isLoadingRoundingRuleSetSimulation,
  } = useSimulatePriceRoundingRuleSet();

  const priceListFormMethods = useForm<PriceListSchema>({
    resolver: yupResolver(priceListSchema(t)),
    mode: 'onChange',
    defaultValues: defaultPriceListValues,
    shouldUnregister: true,
  });

  const prices = priceListFormMethods.watch('prices');

  const simulatePrices = useCallback(
    (simulationConfig: SimulatePriceRoundingRuleConfigDTO) => {
      mutateSimulateRoundingRuleSet({ body: simulationConfig }).catch((err) => {
        console.error(err);
        toast.error(t('rounding.simulation.errors.generic'));
      });
    },
    [mutateSimulateRoundingRuleSet, t, toast]
  );

  const onSubmitSimulation = useCallback(() => {
    const prices = priceListFormMethods.watch('prices');
    const validPrices = prices
      ?.map(({ price }) => Number(price))
      .filter((price) => !Number.isNaN(price));

    if (!validPrices || validPrices.length === 0) {
      toast.error(t('rounding.simulation.errors.noPrices'));
      return;
    }

    simulatePrices({
      prices: validPrices,
      intervals: priceRoundingRuleConfig.ranges.map((range) => range.toDto()),
    });
  }, [priceListFormMethods, priceRoundingRuleConfig.ranges, simulatePrices, t, toast]);

  useEffect(() => {
    if (prices && !hasFetchedInitialSimulation) {
      setHasFetchedInitialSimulation(true);
      simulatePrices({
        prices: prices.map(({ price }) => Number(price)),
        intervals: priceRoundingRuleConfig.ranges.map((range) => range.toDto()),
      });
    }
  }, [
    hasFetchedInitialSimulation,
    onSubmitSimulation,
    priceListFormMethods,
    priceRoundingRuleConfig.ranges,
    prices,
    simulatePrices,
  ]);

  return (
    <Dialog maxWidth="lg" fullWidth open={open} onClose={onClose}>
      <DialogTitle>{renderHeader()}</DialogTitle>

      <DialogContent
        sx={{
          paddingBottom: 0,
          borderBottom: (theme) => `1px solid ${theme.vars.palette.divider}`,
        }}
      >
        <PriceRoundingConfigTable ruleRanges={priceRoundingRuleConfig.ranges} />
      </DialogContent>

      <DialogActions
        sx={{ justifyContent: 'space-between', alignItems: 'flex-end' }}
        disableSpacing
      >
        <FormProvider {...priceListFormMethods}>
          <PriceRoundingSimulations
            simulationResultPrices={simulationResult?.prices}
            submitSimulation={onSubmitSimulation}
            isLoadingRoundingRuleSetSimulation={isLoadingRoundingRuleSetSimulation}
          />
        </FormProvider>
        <Stack direction="row" spacing={1}>
          <OutlinedButton size="small" onClick={onClose}>
            {t('common.cancel')}
          </OutlinedButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default withDialogSuspenseErrorBoundary(ViewRoundingRuleDialog);
